/**
 * The light preview's compiler (light-preview plan F2).
 *
 * `esbuild-wasm` compiles the project's own files in the tab — no Node, no npm install, no Vite —
 * and every bare import is left for the page's import map, which points at the prebuilt vendor
 * bundle. The F0.2 spike measured this at ~450 ms for a 51-file project, against 20–60 s to reach
 * a first preview through the WebContainer.
 *
 * esbuild starts its own web worker, so compiling never blocks the workbench's UI thread.
 */

import type * as EsbuildModule from 'esbuild-wasm';
import {
  isVendored,
  loaderFor,
  packageNameOf,
  resolveImport,
  type PreviewFiles,
} from './moduleGraph';
import {
  assetReferenceOf,
  buildAssetUrlMap,
  inlineAssetReferences,
  inlineAssetsIntoJs,
  type PreviewAssets,
} from './previewAssets';

export interface PreviewCompileResult {
  /** Compiled ESM for the page's module script. */
  js: string;
  /** Plain CSS from the project's stylesheets (Tailwind directives excluded — see below). */
  css: string;
  /** Compile errors, formatted for the error overlay and for the model. */
  errors: string[];
  /** Imports no vendor entry serves; the preview renders without them. */
  unsupported: string[];
  compileMs: number;
  /**
   * The template's render/crash reporter, compiled as a classic script. Vite's `index.html`
   * loaded it before the app so a crash that leaves `#root` empty is still reported; the preview
   * document inlines it for the same reason.
   */
  signalJs?: string;
}

export interface PreviewCompilerOptions {
  /** Where `esbuild.wasm` is served from (our own origin). */
  wasmUrl: string;
  /** Specifier → URL, from the vendor bundle's `importmap.json`. */
  importMap: Readonly<Record<string, string>>;
  /** Defaults to the template's entry. */
  entry?: string;
  /** Defaults to the template's render/crash reporter; skipped when the project has no such file. */
  signalEntry?: string;
  /** Swappable for tests. */
  esbuild?: Pick<typeof EsbuildModule, 'initialize' | 'build'>;
}

/**
 * Removes what plain CSS cannot express, for a page with no Tailwind runtime at all (the unit
 * tests, and a project whose stylesheet is ordinary CSS). With the runtime present the document
 * hands it the stylesheet as Tailwind source instead, which is the only way `@apply` survives —
 * see `buildPreviewDocument`.
 */
export function stripTailwindDirectives(css: string): string {
  return css
    .replace(/^\s*@tailwind\s+[^;]+;\s*$/gm, '')
    .replace(/^\s*@apply\s+[^;]+;\s*$/gm, '')
    .trim();
}

export interface PreviewCompiler {
  compile: (files: PreviewFiles, assets?: PreviewAssets) => Promise<PreviewCompileResult>;
}

/**
 * Generated code sometimes reaches for `React` without importing it — `React.useState`, a
 * `React.FC` annotation left half-converted. Automatic JSX gives no such global, so the preview
 * threw "React is not defined" at first render. This shim is injected into every compile, which
 * makes esbuild bind those references to a real import (the same trick Vite's `jsxInject` uses).
 * Unreferenced, it disappears with the rest of the unused code.
 */
const REACT_SHIM_PATH = 'ideoz-preview-react-shim.js';
const REACT_SHIM_SOURCE = "import React from 'react';\nexport { React };\n";

export async function createPreviewCompiler(
  options: PreviewCompilerOptions,
): Promise<PreviewCompiler> {
  const esbuild = options.esbuild ?? (await import('esbuild-wasm'));
  const entry = options.entry ?? 'src/main.tsx';
  const signalEntry = options.signalEntry ?? 'src/ideoz/renderSignal.ts';

  // esbuild rejects a second `initialize`, and the workbench may mount more than once per page.
  await ensureInitialized(esbuild, options.wasmUrl);

  return {
    async compile(files: PreviewFiles, assets: PreviewAssets = {}): Promise<PreviewCompileResult> {
      const assetUrls = buildAssetUrlMap(assets, files);
      const cssChunks: string[] = [];
      const unsupported = new Set<string>();
      const missing: string[] = [];
      const startedAt = Date.now();

      const projectFiles: EsbuildModule.Plugin = {
        name: 'ideoz-preview-files',
        setup(build) {
          build.onResolve({ filter: /.*/ }, (args) => {
            if (args.path === REACT_SHIM_PATH) {
              return { path: REACT_SHIM_PATH, namespace: 'preview' };
            }
            if (args.kind === 'entry-point') {
              return { path: args.path === signalEntry ? signalEntry : entry, namespace: 'preview' };
            }

            // `import logo from './media/logo.png'` resolves to bytes, not to a module the
            // bundler can parse; the loader below turns it into the asset's URL.
            const assetReference = assetReferenceOf(
              args.path.replace(/^\.\//, '').replace(/^\.\.\//, ''),
            );
            if (assetUrls.has(assetReference)) {
              return { path: assetReference, namespace: 'preview-asset' };
            }

            const resolved = resolveImport(args.path, args.importer, files);
            if (resolved.kind === 'file') return { path: resolved.path, namespace: 'preview' };

            if (resolved.kind === 'missing') {
              missing.push(`${args.path} (imported by ${args.importer || entry})`);
              return { path: args.path, external: true };
            }

            if (!isVendored(resolved.spec, options.importMap)) {
              unsupported.add(packageNameOf(resolved.spec));
            }
            // Left as written: the page's import map resolves it to the vendor bundle.
            return { path: resolved.spec, external: true };
          });

          build.onLoad({ filter: /.*/, namespace: 'preview-asset' }, (args) => ({
            contents: `export default ${JSON.stringify(assetUrls.get(args.path) ?? '')};`,
            loader: 'js',
          }));

          build.onLoad({ filter: /.*/, namespace: 'preview' }, (args) => {
            if (args.path === REACT_SHIM_PATH) {
              return { contents: REACT_SHIM_SOURCE, loader: 'js' };
            }
            const contents = files[args.path] ?? '';
            if (args.path.endsWith('.css')) {
              // Collected for the document's stylesheet, directives and all: the page's Tailwind
              // runtime compiles them. The module itself contributes nothing.
              cssChunks.push(inlineAssetReferences(contents, assetUrls));
              return { contents: '', loader: 'js' };
            }
            return { contents, loader: loaderFor(args.path) };
          });
        },
      };

      /**
       * Built as a module, like the app. As an IIFE esbuild cannot emit its bare imports as ESM
       * and rewrites them to `require()`, which threw "Dynamic require of react is not supported"
       * in the browser. Module scripts still run in document order, so it reports before the app.
       */
      const compileSignal = async (): Promise<string | undefined> => {
        if (files[signalEntry] === undefined) return undefined;
        try {
          const built = await esbuild.build({
            entryPoints: [signalEntry],
            bundle: true,
            write: false,
            format: 'esm',
            target: 'es2020',
            logLevel: 'silent',
            plugins: [projectFiles],
          });
          return built.outputFiles?.[0]?.text;
        } catch {
          // The preview is still worth showing without crash reporting.
          return undefined;
        }
      };

      try {
        const result = await esbuild.build({
          entryPoints: [entry],
          bundle: true,
          write: false,
          format: 'esm',
          target: 'es2020',
          jsx: 'automatic',
          // Records each element's file and line on the JSX call, which is what the preview
          // bridge reads off the fiber to map a clicked element back to its source.
          jsxDev: true,
          sourcemap: 'inline',
          logLevel: 'silent',
          inject: [REACT_SHIM_PATH],
          plugins: [projectFiles],
        });

        return {
          // `/media/logo.png` has no server behind it in a blob document, so the bytes the
          // workspace already holds are inlined instead.
          js: inlineAssetsIntoJs(result.outputFiles?.[0]?.text ?? '', assetUrls),
          css: cssChunks.join('\n\n'),
          errors: [...missing.map((m) => `Could not find ${m}`), ...formatMessages(result.errors)],
          unsupported: [...unsupported],
          compileMs: Date.now() - startedAt,
          signalJs: await compileSignal(),
        };
      } catch (err) {
        return {
          js: '',
          css: cssChunks.join('\n\n'),
          errors: formatBuildFailure(err, missing),
          unsupported: [...unsupported],
          compileMs: Date.now() - startedAt,
        };
      }
    },
  };
}

let initialized: Promise<void> | null = null;

function ensureInitialized(
  esbuild: Pick<typeof EsbuildModule, 'initialize'>,
  wasmURL: string,
): Promise<void> {
  initialized ??= esbuild.initialize({ wasmURL }).catch((err: unknown) => {
    initialized = null;
    throw err;
  });
  return initialized;
}

/** For tests and for a workbench teardown that swaps the wasm URL. */
export function resetPreviewCompiler(): void {
  initialized = null;
}

function formatMessages(messages: readonly EsbuildModule.Message[] | undefined): string[] {
  return (messages ?? []).map((message) => {
    const where = message.location
      ? `${message.location.file}:${message.location.line}:${message.location.column}`
      : 'preview';
    return `${where}: ${message.text}`;
  });
}

function formatBuildFailure(err: unknown, missing: readonly string[]): string[] {
  const failure = err as { errors?: EsbuildModule.Message[] } | undefined;
  const fromEsbuild = formatMessages(failure?.errors);
  const lines = [...missing.map((m) => `Could not find ${m}`), ...fromEsbuild];
  if (lines.length > 0) return lines;
  return [err instanceof Error ? err.message : String(err)];
}
